import React, { useState, useEffect } from 'react';
import { useTimer } from '../contexts/TimerContext';
import { api } from '../config/api';
import './EmailOTPVerificationPopup.css';

const EmailOTPVerificationPopup = ({ 
  isOpen, 
  onClose, 
  onOTPVerified, 
  email,
  userId,
  pendingProfileData
}) => {
  const { formattedTime, isExpired, isActive, startTimer } = useTimer();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const [otpSent, setOtpSent] = useState(false);

  const inputRefs = [
    React.useRef(null), React.useRef(null), React.useRef(null),
    React.useRef(null), React.useRef(null), React.useRef(null)
  ];

  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const showError = (message) => {
    setErrorMessage(message);
    setShowErrorPopup(true);
  };

  /* -------------------- EFFECTS -------------------- */

  useEffect(() => {
    if (isOpen) {
      setOtp(['', '', '', '', '', '']);
      setOtpSent(false);
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !isActive && otpSent) {
      startTimer();
    }
  }, [isOpen, isActive, startTimer, otpSent]);

  useEffect(() => {
    if (isExpired && isOpen) {
      showError('OTP expired! Please request a new OTP.');
      setOtpSent(false);
    }
  }, [isExpired, isOpen]);

  useEffect(() => {
    if (isOpen && inputRefs[0].current) {
      inputRefs[0].current.focus();
    }
  }, [isOpen]);

  // Send OTP when popup opens
  useEffect(() => {
    if (isOpen && email && userId && !otpSent) {
      handleSendOTP();
    }
  }, [isOpen, email, userId, otpSent]);

  /* -------------------- INPUT HANDLERS -------------------- */

  const handleInputChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError(null);

    if (value && index < 5) {
      inputRefs[index + 1].current?.focus();
    }

    if (index === 5 && value && newOtp.every(d => d !== '')) {
      handleVerifyOTP(newOtp.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();

    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      const newOtp = [...otp];
      digits.forEach((d, i) => (newOtp[i] = d));
      setOtp(newOtp);
      handleVerifyOTP(pastedData);
    }
  };

  /* -------------------- SEND OTP -------------------- */

  const handleSendOTP = async () => {
    try {
      setIsSending(true);
      setError(null);

      await api.sendEmailOTP(email, userId);
      setOtpSent(true);
      startTimer();
      showError('OTP has been sent to your registered email address');
    } catch (err) {
      setError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  /* -------------------- VERIFY OTP -------------------- */

  const handleVerifyOTP = async (otpString) => {
    if (otpString.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    try {
      setIsVerifying(true);
      setError(null);

      const result = await api.verifyEmailOTP(email, otpString, userId);

      if (!result.verified) {
        setError(result.error || 'Invalid OTP. Please try again.');
        setOtp(['', '', '', '', '', '']);
        inputRefs[0].current?.focus();
        return;
      }

      // Call the callback with verified token and pending data
      onOTPVerified({
        verified: true,
        token: result.token,
        profileData: pendingProfileData
      });

      onClose();
    } catch (err) {
      setError(err.message || 'Failed to verify OTP.');
      setOtp(['', '', '', '', '', '']);
      inputRefs[0].current?.focus();
    } finally {
      setIsVerifying(false);
    }
  };

  /* -------------------- SUBMIT -------------------- */

  const handleSubmit = (e) => {
    e.preventDefault();
    const otpString = otp.join('');
    if (otpString.length === 6) handleVerifyOTP(otpString);
  };

  const handleResendOTP = async () => {
    setOtp(['', '', '', '', '', '']);
    setOtpSent(false);
    await handleSendOTP();
    inputRefs[0].current?.focus();
  };

  if (!isOpen) return null;

  /* -------------------- UI -------------------- */

  return (
    <div className="email-otp-overlay">
      <div className="email-otp-popup">
        <button className="close-btn" onClick={onClose}>✕</button>

        <div className="timer-display">
          {!otpSent ? 'Sending OTP...' : isExpired ? 'OTP Expired' : `${formattedTime} Mins Remaining`}
        </div>

        <div className="simple-hero">
          <div className="floating-elements">
            <div className="floating-icon">🔐</div>
            <div className="floating-icon">📧</div>
            <div className="floating-icon">✏️</div>
          </div>
        </div>

        <div className="popup-content">
          <h2 className="popup-title">Verify Email for Profile Update</h2>
          <p className="popup-subtitle">
            We've sent a 6-digit OTP to your registered email: <strong>{email}</strong>
          </p>

          {error && <div className="error-message">{error}</div>}

          {isSending ? (
            <div className="sending-otp">
              <div className="spinner"></div>
              <p>Sending OTP to your email...</p>
            </div>
          ) : otpSent ? (
            <form onSubmit={handleSubmit} className="otp-form">
              <div className="otp-input-container">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={inputRefs[index]}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    className="otp-digit-input"
                    disabled={isVerifying}
                  />
                ))}
              </div>

              <button
                type="submit"
                className="proceed-btn"
                disabled={isVerifying || otp.join('').length !== 6}
              >
                {isVerifying ? 'Verifying...' : 'Verify & Update Profile'}
              </button>
            </form>
          ) : null}

          {otpSent && !isSending && (
            <div className="resend-section">
              <p className="resend-text">Didn't receive the OTP?</p>
              <button
                type="button"
                className="resend-btn"
                onClick={handleResendOTP}
                disabled={isVerifying || isExpired}
              >
                Resend OTP
              </button>
            </div>
          )}
        </div>
      </div>

      {showErrorPopup && (
        <div className="error-popup-overlay" onClick={() => setShowErrorPopup(false)}>
          <div className="error-popup-content">
            <div className="error-popup-message">{errorMessage}</div>
            <button onClick={() => setShowErrorPopup(false)}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailOTPVerificationPopup;
